import * as Cmp from "@impero-com/common/utils/comparison";
import { assertNever } from "@impero-com/common/utils/obj";
import { useQuery } from "@tanstack/react-query";

import { getControlProgramsControlProgramIdDatasheetAssignments } from "api/generated/control-program/control-program";
import { DataSheetGroupingAndControlsAssigned } from "api/generated/model";

export const CP_DATASHEET_ASSIGNMENTS_QUERY_KEY = "cpDatasheetAssignments";
export const useCPDatasheetAssignmentsQuery = (
    controlProgramId: number | null
) =>
    useQuery({
        enabled: controlProgramId !== null,
        queryKey: [CP_DATASHEET_ASSIGNMENTS_QUERY_KEY, controlProgramId],
        queryFn: () => {
            if (controlProgramId === null) {
                throw new Error("Expected a control program ID");
            }
            return getControlProgramsControlProgramIdDatasheetAssignments(
                controlProgramId
            ).then((response) => {
                const stringCompare = Cmp.string();
                // Sort in place.
                // Note: `select` below is not cached.
                // Note: it would be nice to do that server side, but it is even more complex than this code.
                const data = response.data;
                data.content.forEach((assignment) => {
                    const dataSheet = data.linkedResources.dataSheets.find(
                        (ds) => ds.id === assignment.resource.dataSheetId
                    )!;
                    const groupingColumns = dataSheet.columns.filter(
                        (col) => col.groupingColumn
                    );
                    assignment.resource.groupings.sort(
                        Cmp.chain(
                            groupingColumns.map(
                                (
                                    col
                                ): Cmp.ComparisonFunction<DataSheetGroupingAndControlsAssigned> => {
                                    switch (col.dataType) {
                                        case "String": {
                                            return Cmp.map(
                                                (grouping) =>
                                                    grouping.columns[col.id] as
                                                        | string
                                                        | null,
                                                Cmp.undefinedLast(stringCompare)
                                            );
                                        }
                                        case "Float": {
                                            return Cmp.map(
                                                (grouping) =>
                                                    grouping.columns[col.id] as
                                                        | number
                                                        | null,
                                                Cmp.undefinedLast(Cmp.primitive)
                                            );
                                        }
                                        case "Entity": {
                                            // Entity columns are not grouping columns, so this
                                            // case won't be reached in practice (groupingColumns
                                            // filter excludes them). Added for type exhaustiveness.
                                            return Cmp.map(
                                                (grouping) =>
                                                    grouping.columns[col.id] as
                                                        | number
                                                        | null,
                                                Cmp.undefinedLast(Cmp.primitive)
                                            );
                                        }
                                        default: {
                                            return assertNever(col.dataType);
                                        }
                                    }
                                }
                            )
                        )
                    );
                });
                return { ...response, data };
            });
        },
        // throwOnError: true, // May want to turn this on later
        select: (response) => response.data,
    });
